import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Shield, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function JoinAdmin() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    if (!token) { setInfo({ valid: false }); return; }
    api.get(`/invites/${token}`).then((r) => setInfo(r.data)).catch(() => setInfo({ valid: false }));
  }, [token]);

  const accept = async () => {
    setAccepting(true);
    try {
      await api.post(`/invites/${token}/accept`);
      await refreshUser();
      toast.success("You're now an admin! Welcome aboard.");
      navigate("/admin");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not accept invite");
    } finally { setAccepting(false); }
  };

  return (
    <div className="mx-auto max-w-md px-4 py-16 text-center" data-testid="join-admin-page">
      <span className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground"><Shield size={26} /></span>
      <h1 className="font-serif text-3xl font-bold">Admin invitation</h1>
      {info === null && <p className="mt-4 text-muted-foreground">Checking your invite…</p>}
      {info && !info.valid && (
        <p className="mt-4 text-muted-foreground" data-testid="join-admin-invalid">
          {info.accepted ? "This invite has already been used." : info.expired ? "This invite has expired." : "This invite link is invalid."}
        </p>
      )}
      {info && info.valid && (
        <>
          <p className="mt-3 text-muted-foreground">{info.invited_by_name || "A CSRAGo admin"} invited you to help manage CSRAGo{info.role === "editor" ? " as an editor" : " as an admin"}.</p>
          {user ? (
            <button onClick={accept} disabled={accepting} data-testid="join-admin-accept"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground disabled:opacity-60">
              {accepting ? <Loader2 size={16} className="animate-spin" /> : <Shield size={16} />} Accept &amp; become a{info.role === "editor" ? "n editor" : "n admin"}
            </button>
          ) : (
            <div className="mt-6">
              <p className="mb-3 text-sm text-muted-foreground">Please sign in or create an account first{info.email ? ` using ${info.email}` : ""} to accept.</p>
              <Link to={`/auth?next=${encodeURIComponent(`/join-admin?token=${token}`)}`} data-testid="join-admin-signin"
                className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground">Sign in / Register</Link>
            </div>
          )}
        </>
      )}
    </div>
  );
}
